import { emptyUser, UserObject } from '../helpers/objects/userObject';
import styles from '../css/users.module.scss';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { EnvPaths, freezeScroll, unfreezeScroll, passwordRegex } from '../helpers/environment';
import CustomTextInput from './customTextInput';
import { useAuth } from '../context/AuthContext';
import CustomDropdown from './customDropdown';
import CustomPasswordInput from './customPasswordInput';
import { useTranslation } from 'react-i18next';

interface UserFormErrors {
  role: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  birthDate: string;
  password: string;
  address: string;
  city: string;
  zipCode: string;
  country: string;
  language: string;
  bankCountry: string;
  currency: string;
  iban: string;
  bankTitle: string;
  bankAddress: string;
  payoutFirstName: string;
  payoutLastName: string;
  payoutCountry: string;
}

const NewUserModal = ({ isOpen, setIsOpen, refetch, setRefetch }: { isOpen: boolean; setIsOpen: (isOpen: boolean) => void; refetch: boolean; setRefetch: (refetch: boolean) => void }) => {
  const [editMode, setEditMode] = useState(true);
  const [editUserObject, setEditUserObject] = useState<UserObject>(emptyUser);
  const [loading, setLoading] = useState(false);
  const admin = useAuth().user;
  const { t } = useTranslation();

  const [formErrors, setFormErrors] = useState<UserFormErrors>({
    role: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    birthDate: '',
    password: '',
    address: '',
    city: '',
    zipCode: '',
    country: '',
    language: '',
    bankCountry: '',
    currency: '',
    iban: '',
    bankTitle: '',
    bankAddress: '',
    payoutFirstName: '',
    payoutLastName: '',
    payoutCountry: '',
  });

  const userRoleOptions = [
    { value: 'user', label: t('editUserModal.user') },
    { value: 'admin', label: t('editUserModal.admin') },
    { value: 'skycop', label: t('editUserModal.skycop') },
  ];
  const [isRoleSelectOpen, setIsRoleSelectOpen] = useState(false);

  const validateName = (name: string): string => {
    if (!name || name.trim().length < 2) {
      return t('newUserModal.validateName.shortLenght');
    }
    if (name.trim().length > 30) {
      return t('newUserModal.validateName.longLenght');
    }
    // Allows letters (including international characters), spaces, hyphens, and apostrophes
    const validPattern = /^[a-zA-ZÀ-ÿ\u0100-\u017F\u0180-\u024F\s\-']+$/;
    if (!validPattern.test(name.trim())) {
      return t('newUserModal.validateName.pattern');
    }
    const hasValidContent = /[a-zA-ZÀ-ÿ\u0100-\u017F\u0180-\u024F]/.test(name.trim());
    if (!hasValidContent) {
      return t('newUserModal.validateName.content');
    }
    return '';
  };

  const validateEmail = (email: string): string => {
    if (!email || email.trim().length === 0) {
      return t('newUserModal.validateEmail.required');
    }
    if (email.trim().length > 254) {
      return t('newUserModal.validateEmail.lenght');
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email.trim())) {
      return t('newUserModal.validateEmail.valid');
    }
    return '';
  };

  const validatePhone = (phone: string): string => {
    if (!phone || phone.trim().length === 0) {
      return t('newUserModal.validatePhone.required');
    }
    const digitsOnly = phone.replace(/\D/g, '');
    if (digitsOnly.length < 7) {
      return t('newUserModal.validatePhone.digits');
    }
    if (digitsOnly.length > 15) {
      return t('newUserModal.validatePhone.length');
    }
    return '';
  };

  const validatePassword = (password: string): string => {
    if (!password || password.length === 0) {
      return t('newUserModal.validatePassword.required');
    }
    if (!passwordRegex.test(password)) {
      return t('newUserModal.validatePassword.regex');
    }
    return '';
  };

  const validateBirthDate = (birthDate: Date | string): string => {
    if (!birthDate) {
      return t('newUserModal.validateBirthDate.required');
    }
    const date = new Date(birthDate);
    const today = new Date();
    const minDate = new Date(today.getFullYear() - 120, today.getMonth(), today.getDate());

    if (isNaN(date.getTime())) {
      return t('newUserModal.validateBirthDate.valid');
    }
    if (date < minDate) {
      return t('newUserModal.validateBirthDate.limit');
    }
    return '';
  };

  const validateOptionalText = (value: string, fieldName: string, minLength = 2, maxLength = 100): string => {
    if (value && value.trim().length > 0) {
      if (value.trim().length < minLength) {
        return t('newUserModal.validateOptionalText.short', {
          fieldName: fieldName,
          minLength: minLength,
        });
      }
      if (value.trim().length > maxLength) {
        return t('newUserModal.validateOptionalText.long', {
          fieldName: fieldName,
          maxLength: maxLength,
        });
      }
    }
    return '';
  };

  const validateOptionalName = (value: string, fieldName: string): string => {
    if (value && value.trim().length > 0) {
      // Allows letters (including international characters), spaces, hyphens, and apostrophes
      const validPattern = /^[a-zA-ZÀ-ÿ\u0100-\u017F\u0180-\u024F\s\-']+$/;
      if (!validPattern.test(value.trim())) {
        return t('newUserModal.validateOptionalName.pattern', {
          fieldName: fieldName,
        });
      }
      if (value.trim().length < 2) {
        return t('newUserModal.validateOptionalName.short', {
          fieldName: fieldName,
        });
      }
      if (value.trim().length > 50) {
        return t('newUserModal.validateOptionalName.long', {
          fieldName: fieldName,
        });
      }
    }
    return '';
  };

  const validateIban = (iban: string): string => {
    if (iban && iban.trim().length > 0) {
      const ibanPattern = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{4}[0-9]{7}([A-Z0-9]?){0,16}$/;
      if (!ibanPattern.test(iban.trim().replace(/\s/g, ''))) {
        return t('newUserModal.validateIban.valid');
      }
    }
    return '';
  };

  const validateField = (field: keyof UserFormErrors, value: any) => {
    let error = '';

    switch (field) {
      case 'firstName':
      case 'lastName':
        error = validateName(value);
        break;
      case 'email':
        error = validateEmail(value);
        break;
      case 'phoneNumber':
        error = validatePhone(value);
        break;
      case 'password':
        error = validatePassword(value);
        break;
      case 'birthDate':
        error = validateBirthDate(value);
        break;
      case 'address':
        error = validateOptionalText(value, t('newUserModal.validateField.address'), 5, 200);
        break;
      case 'city':
      case 'country':
      case 'language':
      case 'bankCountry':
      case 'currency':
      case 'bankTitle':
      case 'payoutCountry':
        error = validateOptionalText(value, field.charAt(0).toUpperCase() + field.slice(1));
        break;
      case 'zipCode':
        error = validateOptionalText(value, t('newUserModal.validateField.zipCode'), 3, 10);
        break;
      case 'iban':
        error = validateIban(value);
        break;
      case 'bankAddress':
        error = validateOptionalText(value, t('newUserModal.validateField.bankAddress'), 5, 200);
        break;
      case 'payoutFirstName':
      case 'payoutLastName':
        error = validateOptionalName(value, field === 'payoutFirstName' ? t('newUserModal.validateField.payoutFirstName') : t('newUserModal.validateField.payoutLastName'));
        break;
    }

    setFormErrors((prev) => ({ ...prev, [field]: error }));
    return error;
  };

  const validateMandatoryFields = (): boolean => {
    const mandatoryFields = ['firstName', 'lastName', 'email', 'phoneNumber', 'birthDate', 'password'];
    let hasErrors = false;
    const newErrors = { ...formErrors };

    mandatoryFields.forEach((field) => {
      const fieldKey = field as keyof UserFormErrors;
      let value;

      if (field === 'birthDate') {
        value = editUserObject.birthDate;
      } else {
        value = editUserObject[field as keyof UserObject];
      }

      const error = validateField(fieldKey, value);
      if (error) {
        newErrors[fieldKey] = error;
        hasErrors = true;
      }
    });

    setFormErrors(newErrors);

    if (hasErrors) {
      toast.error(t('newUserModal.validateMandatoryFields.toast.error'));
    }

    return !hasErrors;
  };

  const handleUpdate = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateMandatoryFields()) {
      return;
    }

    setLoading(true);
    toast.loading(t('newUserModal.handleUpdate.toast.loading'));
    const { address, city, zipCode, country, language } = editUserObject;

    const isUserInfoComplete = !!address && !!city && !!zipCode && !!country && !!language;

    const { _id, ...userDataWithoutId } = editUserObject;

    axios
      .post(
        `${EnvPaths.SERVER_URL}/admin/create-new-user`,
        {
          data: {
            ...userDataWithoutId,
            firstName: editUserObject.firstName?.trim(),
            lastName: editUserObject.lastName?.trim(),
            email: editUserObject.email?.trim().toLowerCase(),
            phoneNumber: editUserObject.phoneNumber?.trim(),
            isUserInfoComplete,
            isVerified: true,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${admin?.token}`,
          },
        }
      )
      .then(() => {
        toast.dismiss();
        toast.success(t('newUserModal.handleUpdate.toast.success'));
        setLoading(false);
        setIsOpen(false);
        setEditMode(false);
        setRefetch(!refetch);

        setEditUserObject(emptyUser);
        setFormErrors({
          role: '',
          firstName: '',
          lastName: '',
          email: '',
          phoneNumber: '',
          birthDate: '',
          password: '',
          address: '',
          city: '',
          zipCode: '',
          country: '',
          language: '',
          bankCountry: '',
          currency: '',
          iban: '',
          bankTitle: '',
          bankAddress: '',
          payoutFirstName: '',
          payoutLastName: '',
          payoutCountry: '',
        });
      })
      .catch((error) => {
        console.error(error);
        toast.dismiss();
        if (error?.status === 409) {
          toast.error(t('newUserModal.handleUpdate.toast.error400'));
        } else {
          toast.error(t('newUserModal.handleUpdate.toast.error500'));
        }
        setLoading(false);
      });
  };

  useEffect(() => {
    if (isOpen) {
      freezeScroll();
    } else {
      unfreezeScroll();
    }
  }, [isOpen]);

  return (
    <div className={`${styles.editUserModalContainer} ${isOpen ? styles.open : ''}`}>
      <form onSubmit={handleUpdate} className={`${styles.editUserModalContent}`}>
        <button
          type="button"
          disabled={loading}
          onClick={() => {
            setEditMode(false);
            setIsOpen(false);
            setEditUserObject(emptyUser);
            setFormErrors({
              role: '',
              firstName: '',
              lastName: '',
              email: '',
              phoneNumber: '',
              birthDate: '',
              password: '',
              address: '',
              city: '',
              zipCode: '',
              country: '',
              language: '',
              bankCountry: '',
              currency: '',
              iban: '',
              bankTitle: '',
              bankAddress: '',
              payoutFirstName: '',
              payoutLastName: '',
              payoutCountry: '',
            });
          }}
          className={`${styles.closeButton}`}
        >
          <img src="/assets/images/close-icon.svg" alt="Close" />
        </button>
        <h2>{t('newUserModal.title')}</h2>
        <hr />
        <div className={styles.editUserInfoSection}>
          <CustomDropdown
            width="100%"
            isOpen={isRoleSelectOpen}
            onToggle={() => setIsRoleSelectOpen((prev) => !prev)}
            value={userRoleOptions.find((opt) => opt.value === editUserObject?.role) ?? { value: 'user', label: 'User' }}
            onChange={(e) => {
              setEditUserObject({
                ...editUserObject,
                role: e.value as 'user' | 'admin' | 'skycop',
              });
            }}
            options={userRoleOptions}
          />
        </div>
        <hr />
        <h3>{t('newUserModal.personalInformation.title')}</h3>
        <div className={styles.editUserInfoSection}>
          <div className={styles.inputContainer}>
            <label htmlFor="firstName">{t('newUserModal.personalInformation.firstName')}</label>
            <CustomTextInput
              type="text"
              id="firstName"
              disabled={!editMode}
              value={editUserObject?.firstName}
              onChange={(e) => {
                const value = e.target.value;
                setEditUserObject({ ...editUserObject, firstName: value });
                validateField('firstName', value);
              }}
              error={formErrors.firstName}
            />
            {formErrors.firstName && (
              <span className="error-text" style={{ color: 'red', fontSize: '12px' }}>
                {formErrors.firstName}
              </span>
            )}
          </div>

          <div className={styles.inputContainer}>
            <label htmlFor="lastName">{t('newUserModal.personalInformation.lastName')}</label>
            <CustomTextInput
              type="text"
              id="lastName"
              disabled={!editMode}
              value={editUserObject?.lastName}
              onChange={(e) => {
                const value = e.target.value;
                setEditUserObject({ ...editUserObject, lastName: value });
                validateField('lastName', value);
              }}
              error={formErrors.lastName}
            />
            {formErrors.lastName && (
              <span className="error-text" style={{ color: 'red', fontSize: '12px' }}>
                {formErrors.lastName}
              </span>
            )}
          </div>

          <div className={styles.inputContainer}>
            <label htmlFor="email">{t('newUserModal.personalInformation.email')}</label>
            <CustomTextInput
              type="email"
              id="email"
              disabled={!editMode}
              value={editUserObject?.email}
              onChange={(e) => {
                const value = e.target.value;
                setEditUserObject({ ...editUserObject, email: value });
                validateField('email', value);
              }}
              error={formErrors.email}
            />
            {formErrors.email && (
              <span className="error-text" style={{ color: 'red', fontSize: '12px' }}>
                {formErrors.email}
              </span>
            )}
          </div>

          <div className={styles.inputContainer}>
            <label htmlFor="phoneNumber">{t('newUserModal.personalInformation.phoneNumber')}</label>
            <CustomTextInput
              type="text"
              id="phoneNumber"
              disabled={!editMode}
              value={editUserObject?.phoneNumber}
              onChange={(e) => {
                const value = e.target.value;
                setEditUserObject({ ...editUserObject, phoneNumber: value });
                validateField('phoneNumber', value);
              }}
              error={formErrors.phoneNumber}
            />
            {formErrors.phoneNumber && (
              <span className="error-text" style={{ color: 'red', fontSize: '12px' }}>
                {formErrors.phoneNumber}
              </span>
            )}
          </div>

          <div className={styles.inputContainer}>
            <label htmlFor="birthDate">{t('newUserModal.personalInformation.birthDate')}</label>
            <CustomTextInput
              type="date"
              id="birthDate"
              disabled={!editMode}
              max={new Date().toISOString().split('T')[0]}
              value={editUserObject?.birthDate ? new Date(editUserObject.birthDate).toISOString().split('T')[0] : ''}
              onChange={(e) => {
                const rawValue = e.target.value;

                if (!rawValue) {
                  setEditUserObject({ ...editUserObject, birthDate: undefined });
                  validateField('birthDate', null);
                  return;
                }

                const parsedDate = new Date(rawValue);

                if (!isNaN(parsedDate.getTime())) {
                  setEditUserObject({ ...editUserObject, birthDate: parsedDate });
                  validateField('birthDate', parsedDate);
                } else {
                  setEditUserObject({ ...editUserObject, birthDate: undefined });
                  validateField('birthDate', null);
                }
              }}
              error={formErrors.birthDate}
            />
            {formErrors.birthDate && (
              <span className="error-text" style={{ color: 'red', fontSize: '12px' }}>
                {formErrors.birthDate}
              </span>
            )}
          </div>

          <div className={styles.inputContainer}>
            <label htmlFor="newPassword">{t('newUserModal.personalInformation.password')}</label>
            <CustomPasswordInput
              id="newPassword"
              onChange={(e) => {
                const value = e.target.value;
                setEditUserObject({ ...editUserObject, password: value });
                validateField('password', value);
              }}
              error={formErrors.password}
            />
            {formErrors.password && (
              <span className="error-text" style={{ color: 'red', fontSize: '12px' }}>
                {formErrors.password}
              </span>
            )}
          </div>
        </div>

        <hr />
        <h3>{t('newUserModal.residentalInformation.title')}</h3>
        <div className={styles.editUserInfoSection}>
          <div className={styles.inputContainer}>
            <label htmlFor="address">{t('newUserModal.residentalInformation.address')}</label>
            <CustomTextInput
              type="text"
              id="address"
              disabled={!editMode}
              value={editUserObject?.address}
              onChange={(e) => {
                const value = e.target.value;
                setEditUserObject({ ...editUserObject, address: value });
                validateField('address', value);
              }}
              error={formErrors.address}
            />
            {formErrors.address && (
              <span className="error-text" style={{ color: 'red', fontSize: '12px' }}>
                {formErrors.address}
              </span>
            )}
          </div>

          <div className={styles.inputContainer}>
            <label htmlFor="city">{t('newUserModal.residentalInformation.city')}</label>
            <CustomTextInput
              type="text"
              id="city"
              disabled={!editMode}
              value={editUserObject?.city}
              onChange={(e) => {
                const value = e.target.value;
                setEditUserObject({ ...editUserObject, city: value });
                validateField('city', value);
              }}
              error={formErrors.city}
            />
            {formErrors.city && (
              <span className="error-text" style={{ color: 'red', fontSize: '12px' }}>
                {formErrors.city}
              </span>
            )}
          </div>

          <div className={styles.inputContainer}>
            <label htmlFor="zipCode">{t('newUserModal.residentalInformation.zipCode')}</label>
            <CustomTextInput
              type="text"
              id="zipCode"
              disabled={!editMode}
              value={editUserObject?.zipCode}
              onChange={(e) => {
                const value = e.target.value;
                setEditUserObject({ ...editUserObject, zipCode: value });
                validateField('zipCode', value);
              }}
              error={formErrors.zipCode}
            />
            {formErrors.zipCode && (
              <span className="error-text" style={{ color: 'red', fontSize: '12px' }}>
                {formErrors.zipCode}
              </span>
            )}
          </div>

          <div className={styles.inputContainer}>
            <label htmlFor="country">{t('newUserModal.residentalInformation.countryCode')}</label>
            <CustomTextInput
              type="text"
              id="country"
              disabled={!editMode}
              value={editUserObject?.country}
              onChange={(e) => {
                const value = e.target.value;
                setEditUserObject({ ...editUserObject, country: value });
                validateField('country', value);
              }}
              error={formErrors.country}
            />
            {formErrors.country && (
              <span className="error-text" style={{ color: 'red', fontSize: '12px' }}>
                {formErrors.country}
              </span>
            )}
          </div>

          <div className={styles.inputContainer}>
            <label htmlFor="language">{t('newUserModal.residentalInformation.languageCode')}</label>
            <CustomTextInput
              type="text"
              id="language"
              disabled={!editMode}
              value={editUserObject?.language}
              onChange={(e) => {
                const value = e.target.value;
                setEditUserObject({ ...editUserObject, language: value });
                validateField('language', value);
              }}
              error={formErrors.language}
            />
            {formErrors.language && (
              <span className="error-text" style={{ color: 'red', fontSize: '12px' }}>
                {formErrors.language}
              </span>
            )}
          </div>
        </div>

        <hr />
        <h3>{t('newUserModal.paymentInformation.title')}</h3>
        <div className={styles.editUserInfoSection}>
          <div className={styles.inputContainer}>
            <label htmlFor="bankCountry">{t('newUserModal.residentalInformation.bankCountry')}</label>
            <CustomTextInput
              type="text"
              id="bankCountry"
              disabled={!editMode}
              value={editUserObject?.paymentInfo?.bankCountry}
              onChange={(e) => {
                const value = e.target.value;
                setEditUserObject({
                  ...editUserObject,
                  paymentInfo: { ...editUserObject?.paymentInfo, bankCountry: value },
                });
                validateField('bankCountry', value);
              }}
              error={formErrors.bankCountry}
            />
            {formErrors.bankCountry && (
              <span className="error-text" style={{ color: 'red', fontSize: '12px' }}>
                {formErrors.bankCountry}
              </span>
            )}
          </div>

          <div className={styles.inputContainer}>
            <label htmlFor="currency">{t('newUserModal.residentalInformation.currency')}</label>
            <CustomTextInput
              type="text"
              id="currency"
              disabled={!editMode}
              value={editUserObject?.paymentInfo?.currency}
              onChange={(e) => {
                const value = e.target.value;
                setEditUserObject({
                  ...editUserObject,
                  paymentInfo: { ...editUserObject?.paymentInfo, currency: value },
                });
                validateField('currency', value);
              }}
              error={formErrors.currency}
            />
            {formErrors.currency && (
              <span className="error-text" style={{ color: 'red', fontSize: '12px' }}>
                {formErrors.currency}
              </span>
            )}
          </div>

          <div className={styles.inputContainer}>
            <label htmlFor="iban">{t('newUserModal.residentalInformation.iban')}</label>
            <CustomTextInput
              type="text"
              id="iban"
              disabled={!editMode}
              value={editUserObject?.paymentInfo?.iban}
              onChange={(e) => {
                const value = e.target.value;
                setEditUserObject({
                  ...editUserObject,
                  paymentInfo: { ...editUserObject?.paymentInfo, iban: value },
                });
                validateField('iban', value);
              }}
              error={formErrors.iban}
            />
            {formErrors.iban && (
              <span className="error-text" style={{ color: 'red', fontSize: '12px' }}>
                {formErrors.iban}
              </span>
            )}
          </div>

          <div className={styles.inputContainer}>
            <label htmlFor="bankTitle">{t('newUserModal.residentalInformation.bankTitle')}</label>
            <CustomTextInput
              type="text"
              id="bankTitle"
              disabled={!editMode}
              value={editUserObject?.paymentInfo?.bankTitle}
              onChange={(e) => {
                const value = e.target.value;
                setEditUserObject({
                  ...editUserObject,
                  paymentInfo: { ...editUserObject?.paymentInfo, bankTitle: value },
                });
                validateField('bankTitle', value);
              }}
              error={formErrors.bankTitle}
            />
            {formErrors.bankTitle && (
              <span className="error-text" style={{ color: 'red', fontSize: '12px' }}>
                {formErrors.bankTitle}
              </span>
            )}
          </div>

          <div className={styles.inputContainer}>
            <label htmlFor="bankAddress">{t('newUserModal.residentalInformation.bankAddress')}</label>
            <CustomTextInput
              type="text"
              id="bankAddress"
              disabled={!editMode}
              value={editUserObject?.paymentInfo?.bankAddress}
              onChange={(e) => {
                const value = e.target.value;
                setEditUserObject({
                  ...editUserObject,
                  paymentInfo: { ...editUserObject?.paymentInfo, bankAddress: value },
                });
                validateField('bankAddress', value);
              }}
              error={formErrors.bankAddress}
            />
            {formErrors.bankAddress && (
              <span className="error-text" style={{ color: 'red', fontSize: '12px' }}>
                {formErrors.bankAddress}
              </span>
            )}
          </div>

          <div className={styles.inputContainer}>
            <label htmlFor="payoutFirstName">{t('newUserModal.residentalInformation.payoutFirstName')}</label>
            <CustomTextInput
              type="text"
              id="payoutFirstName"
              disabled={!editMode}
              value={editUserObject?.paymentInfo?.firstName}
              onChange={(e) => {
                const value = e.target.value;
                setEditUserObject({
                  ...editUserObject,
                  paymentInfo: { ...editUserObject?.paymentInfo, firstName: value },
                });
                validateField('payoutFirstName', value);
              }}
              error={formErrors.payoutFirstName}
            />
            {formErrors.payoutFirstName && (
              <span className="error-text" style={{ color: 'red', fontSize: '12px' }}>
                {formErrors.payoutFirstName}
              </span>
            )}
          </div>

          <div className={styles.inputContainer}>
            <label htmlFor="payoutLastName">{t('newUserModal.residentalInformation.payoutLastName')}</label>
            <CustomTextInput
              type="text"
              id="payoutLastName"
              disabled={!editMode}
              value={editUserObject?.paymentInfo?.lastName}
              onChange={(e) => {
                const value = e.target.value;
                setEditUserObject({
                  ...editUserObject,
                  paymentInfo: { ...editUserObject?.paymentInfo, lastName: value },
                });
                validateField('payoutLastName', value);
              }}
              error={formErrors.payoutLastName}
            />
            {formErrors.payoutLastName && (
              <span className="error-text" style={{ color: 'red', fontSize: '12px' }}>
                {formErrors.payoutLastName}
              </span>
            )}
          </div>

          <div className={styles.inputContainer}>
            <label htmlFor="payoutCountry">{t('newUserModal.residentalInformation.payoutCountry')}</label>
            <CustomTextInput
              type="text"
              id="payoutCountry"
              disabled={!editMode}
              value={editUserObject?.paymentInfo?.userCountry}
              onChange={(e) => {
                const value = e.target.value;
                setEditUserObject({
                  ...editUserObject,
                  paymentInfo: { ...editUserObject?.paymentInfo, userCountry: value },
                });
                validateField('payoutCountry', value);
              }}
              error={formErrors.payoutCountry}
            />
            {formErrors.payoutCountry && (
              <span className="error-text" style={{ color: 'red', fontSize: '12px' }}>
                {formErrors.payoutCountry}
              </span>
            )}
          </div>
        </div>

        {editMode && (
          <button type="submit" disabled={loading} className="primaryButton">
            {loading ? t('newUserModal.loading.creating') : t('newUserModal.loading.save')}
          </button>
        )}
      </form>
    </div>
  );
};

export default NewUserModal;
